import { Flex, Tag } from '@chakra-ui/react';
import { unionBy, isEqual } from 'lodash';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { CommitmentMechanicsFormData } from './types';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  getFormValues,
  getFormValuesFromCommitment,
} from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/dataMappings/getFormValues';
import { setFormValues } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/dataMappings/setFormValues';
import { useCommitmentDiffResult } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/hooks';
import { LenderTable } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/LenderTable/LenderTable';
import { ProductsTable } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/ProductsTable';
import { ServicingFeeTable } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/ServicingFeeTable';
import { AutoSave } from '~/core/routes/settings/implementations/common/AutoSave';
import {
  useCreditAgreementStore,
  useGetCreditAgreementLazy,
} from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import {
  DateInput,
  WholePageDiffOutline,
} from '~/core/routes/settings/implementations/common/DiffInputs';
import { EditableTitle } from '~/core/routes/settings/implementations/common/Inputs';

type CommitmentMechanicsProps = { commitmentId: string };
export const CommitmentMechanics = ({
  commitmentId,
}: CommitmentMechanicsProps) => {
  const getCreditAgreementOrThrow = useGetCreditAgreementLazy();

  const isSublimit = useCreditAgreementStore((store) => {
    const commitment = store.creditAgreement?.loanMechanicsV1?.commitments.find(
      (c) => c.key === commitmentId,
    );
    return Boolean(commitment?.limit.sublimit);
  }, isEqual);

  const diffFormValues = useCreditAgreementStore((store) => {
    const creditAgreement = store.creditAgreement;
    const previousCreditAgreement = store.previousCreditAgreement;

    const currentCommitment = creditAgreement?.loanMechanicsV1?.commitments.find(
      (c) => c.key === commitmentId,
    );
    const previousCommitment = previousCreditAgreement?.loanMechanicsV1?.commitments.find(
      (c) => c.key === commitmentId,
    );

    if (
      previousCommitment &&
      previousCreditAgreement &&
      currentCommitment &&
      creditAgreement
    ) {
      const previousFormValues = getFormValuesFromCommitment(
        previousCommitment,
        previousCreditAgreement,
      );

      const currentFormValues = getFormValuesFromCommitment(
        currentCommitment,
        creditAgreement,
      );

      // In view diff mode, with previous and current commitments, merge all arrays (so deleted items can be rendered in form)
      return {
        ...previousFormValues,
        products: unionBy(
          currentFormValues.products,
          previousFormValues.products,
          (product) => product?.pricingOptionKey,
        ),
        lenders: unionBy(
          currentFormValues.lenders,
          previousFormValues.lenders,
          (lender) => lender?.lenderId,
        ),
        servicingFees: unionBy(
          currentFormValues.servicingFees,
          previousFormValues.servicingFees,
          (fee) => fee?.lenderId,
        ),
      };
    }

    return undefined;
  }, isEqual);

  const form = useForm<CommitmentMechanicsFormData>({
    defaultValues: {
      wipForm: getFormValues(commitmentId, getCreditAgreementOrThrow()),
    },
    mode: 'onTouched',
  });

  const diffResult = useCommitmentDiffResult(commitmentId);

  const diffKey = useCreditAgreementStore((store) =>
    store.isEditing ? 'wipForm' : 'diffForm',
  );

  useEffect(() => {
    form.setValue('diffForm', diffFormValues);
  }, [form, diffFormValues]);

  return (
    <FormProvider {...form}>
      <AutoSave<CommitmentMechanicsFormData> onSave={setFormValues} />
      <form>
        <WholePageDiffOutline diff={diffResult.diffResult}>
          <Flex direction="column" gap="2rem">
            <Flex direction="column" gap="1rem">
              <Flex alignItems="center" gap="1rem">
                <EditableTitle<CommitmentMechanicsFormData>
                  name="wipForm.commitmentName"
                  required
                />
                {isSublimit && (
                  <Tag variant="solid-muted" colorScheme="blue">
                    Sublimit
                  </Tag>
                )}
              </Flex>
              <Typography variant="base-muted">
                Define the lenders, their commitment amounts, servicing fees,
                and the products associated with this commitment. Select
                &apos;Review Changes&apos; to submit for approval.
              </Typography>
            </Flex>
            {/* Need to use "key" here to reset the useFieldArray hook inside, since
                useFieldArray does not support dynamic 'name' fields */}
            <LenderTable key={`lenderTable-${diffKey}`} formKey={diffKey} />

            <Flex>
              <DateInput<CommitmentMechanicsFormData>
                name="wipForm.maturityDate"
                diffName="diffForm.maturityDate"
                label="Maturity Date"
                helperText="The commitment date cannot exceed the facility's maturity date."
              />
            </Flex>
            {/* Need to use "key" here to reset the useFieldArray hook inside, since
                useFieldArray does not support dynamic 'name' fields */}
            <ServicingFeeTable
              key={`servicingFeeTable-${diffKey}`}
              formKey={diffKey}
            />
            {/* Need to use "key" here to reset the useFieldArray hook inside, since
                useFieldArray does not support dynamic 'name' fields */}
            <ProductsTable key={`productsTable-${diffKey}`} formKey={diffKey} />
          </Flex>
        </WholePageDiffOutline>
      </form>
    </FormProvider>
  );
};
